/**
 * Settings (OutlierOS /settings replica, docs/legacy-ui/03 §10), card order kept: Chrome extension ·
 * Connected browsers (was "Active Device Bindings") · AI connectors (Phase 7) · Support, then the new Account
 * card (export my data, delete my account; SPEC §16), with a Privacy card before it (DPDP, decision 44), and Shared report links (QA 2026-10-16). Pairing is the signed /connect flow: no tokens to copy.
 */
import { useEffect, useState } from 'react';
import { Bell, Download, Laptop, LifeBuoy, Link2, Plug, Puzzle, ShieldCheck, UserX } from 'lucide-react';
import { PrivacySettings } from '../components/Privacy';
import { NotificationSettings } from '../components/NotificationSettings';
import { SharedLinks } from '../components/SharedLinks';
import { AddToChrome } from '../components/AddToChrome';
import { BRAND } from '@vw/core/src/brand';
import { Button, ConfirmDialog, Pill, Spinner, copyText, toast } from '@vw/ui';
import { api, ApiError } from '../api';
import { downloadText, stamp } from '../download';
import { ago } from '../format';
import { plural } from '@vw/core/src/plural';
import { useSession } from '../session';
import { Link } from '../router';

interface ConnectedApp { id: string; name: string; scopes: string[]; connectedAt: number | null }
interface Device { id: string; name: string; created_at: number; last_used_at: number | null }

export function Settings() {
  const { me, settings } = useSession();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [revoke, setRevoke] = useState<Device | null>(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [apps, setApps] = useState<ConnectedApp[] | null>(null);
  const [revokeApp, setRevokeApp] = useState<ConnectedApp | null>(null);
  const mcpAllowed = !!me.plan.limits?.mcp || me.user.role === 'admin';
  const loadApps = () => api<{ apps: ConnectedApp[] }>('/api/connected-apps').then((r) => setApps(r.apps), () => setApps([]));
  useEffect(() => { if (mcpAllowed) void loadApps(); }, [mcpAllowed]);
  useEffect(() => { if (location.hash === '#ai') document.getElementById('ai')?.scrollIntoView(); }, []);
  const limit = Number(me.plan.limits?.pairedBrowsers ?? 1);
  const load = () => api<{ devices: Device[] }>('/api/devices').then((r) => setDevices(r.devices), () => setDevices([]));
  useEffect(() => { void load(); }, []);
  useEffect(() => { if (location.hash === '#extension' || location.hash === '#shares') document.getElementById(location.hash.slice(1))?.scrollIntoView(); }, []);

  async function disconnect(d: Device) {
    setBusy(true);
    try {
      await api(`/api/devices/${d.id}/revoke`, { method: 'POST' });
      toast.success(`${d.name} disconnected`, { description: 'The extension in that browser is signed out now.' });
      await load();
    } catch (e) {
      toast.error('Could not disconnect', { description: (e as Error).message });
    } finally {
      setBusy(false);
      setRevoke(null);
    }
  }

  async function exportData() {
    setExporting(true);
    try {
      const data = await api<unknown>('/api/account/export');
      downloadText(`${BRAND.slug}-account-${stamp()}.json`, JSON.stringify(data, null, 2), 'application/json');
      toast.success('Your data was downloaded');
    } catch (e) {
      toast.error('Could not export your data', { description: (e as Error).message });
    } finally {
      setExporting(false);
    }
  }

  async function deleteAccount(typed: string) {
    setDeleteBusy(true);
    try {
      await api('/api/account/delete', { method: 'POST', json: { confirm: typed } });
      location.href = me.links?.website ?? '/';
    } catch (e) {
      if (e instanceof ApiError && e.code === 'reauth_required') {
        // A fresh Google sign-in, then back here to finish.
        location.href = `/signin?return_to=${encodeURIComponent('/settings#account')}`;
        return;
      }
      const paid = e instanceof ApiError && e.status === 409;
      toast.error(paid ? 'Cancel your plan first' : 'Could not delete your account', {
        description: (e as Error).message,
        ...(paid ? { action: { label: 'Billing', onClick: () => { location.href = '/billing'; } } } : {}),
      });
      setDeleteBusy(false);
      setDeleting(false);
    }
  }

  const card = 'space-y-4 rounded-3xl border border-border bg-card p-6 shadow-xs md:p-8';
  const head = (icon: React.ReactNode, title: string, sub: string, right?: React.ReactNode) => (
    <div className="flex flex-wrap items-start gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">{icon}</span>
      <div className="min-w-0 flex-1"><h2 className="font-extrabold">{title}</h2><p className="text-sm text-muted-foreground">{sub}</p></div>
      {right}
    </div>
  );
  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Account & Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your Chrome extension, connected browsers, AI connectors, shared report links, support and account.</p>
      </div>

      <section id="extension" className={card}>
        {head(<Puzzle className="h-5 w-5" />, 'Chrome extension', `${BRAND.name} shows Wise Score badges on any creator's Reels tab and opens transcripts in Chrome's side panel.`)}
        <ol className="list-decimal space-y-1.5 pl-5 text-sm">
          <li>Install the {BRAND.name} extension in Chrome on your computer.</li>
          <li>Click its icon to open the side panel, then press <b>Connect</b>.</li>
          <li>A tab opens here: check the code matches and press <b>Approve</b>.</li>
        </ol>
        <AddToChrome url={settings?.extensionStoreUrl} />
        <p className="text-xs text-muted-foreground">Connecting uses a key that never leaves your browser. There is no token to copy or paste.</p>
      </section>

      <section className={card}>
        {head(<Laptop className="h-5 w-5" />, 'Connected browsers', `Your plan allows ${plural(limit, 'connected browser')}. Disconnect one to connect another.`,
          <Pill tone="primary">{devices?.length ?? '…'} / {limit} connected</Pill>)}
        {!devices ? <Spinner /> : !devices.length ? (
          <p className="rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground">No browsers connected yet. Open the extension's side panel and press Connect.</p>
        ) : (
          <ul className="divide-y divide-border rounded-2xl border border-border">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center gap-3 p-4">
                <span className="text-xl" aria-hidden="true">💻</span>
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{d.name}</div>
                  <div className="text-xs text-muted-foreground">Connected {ago(d.created_at)}{d.last_used_at ? ` · last used ${ago(d.last_used_at)}` : ''}</div></div>
                <Button variant="outline-danger" size="sm" onClick={() => setRevoke(d)}>Disconnect</Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section id="ai" className={card}>
        {head(<Plug className="h-5 w-5" />, 'AI connectors (MCP)', 'Use your research inside Claude, ChatGPT and Cursor by signing in with Google. No API keys.',
          mcpAllowed ? <Link to="/ai" className="text-xs font-semibold text-primary">Setup guide →</Link> : <Pill tone="muted">Pro</Pill>)}
        {me.links?.mcp && (
          <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 p-3">
            <code className="min-w-0 flex-1 truncate font-mono text-xs">{me.links.mcp}</code>
            <Button size="sm" variant="secondary" onClick={() => void copyText(me.links!.mcp!, { success: 'Connector URL copied' })}>Copy</Button>
          </div>
        )}
        {!mcpAllowed ? <p className="text-sm text-muted-foreground">AI connectors are part of the Pro plan. <Link to="/billing" className="font-semibold text-primary">See plans →</Link></p>
          : !apps ? <Spinner /> : !apps.length ? (
            <p className="rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground">No AI apps connected yet. Add the URL above as a connector in your AI app and sign in with Google.</p>
          ) : (
            <ul className="divide-y divide-border rounded-2xl border border-border">
              {apps.map((a) => (
                <li key={a.id} className="flex items-center gap-3 p-4">
                  <span className="text-xl" aria-hidden="true">🤖</span>
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{a.name}</div>
                    <div className="text-xs text-muted-foreground">{a.connectedAt ? `Connected ${ago(a.connectedAt)} · ` : ''}{plural(a.scopes.length, 'permission')}</div></div>
                  <Button variant="outline-danger" size="sm" onClick={() => setRevokeApp(a)}>Disconnect</Button>
                </li>
              ))}
            </ul>
          )}
      </section>

      <section id="notifications" className={card}>
        {head(<Bell className="h-5 w-5" />, 'Notifications', 'What shows up under the bell at the top of the page. Untick a kind to stop it.')}
        <NotificationSettings />
      </section>

      <section id="shares" className={card}>
        {head(<Link2 className="h-5 w-5" />, 'Shared report links', 'Public links to creator reports you shared. Anyone with a link can see the report (not its transcripts or hooks) until you turn it off.')}
        <SharedLinks />
      </section>

      <section className={card}>
        {head(<LifeBuoy className="h-5 w-5" />, 'Need help?', 'Questions about your account, browsers or billing: email us and we will get back to you.')}
        <a className="text-sm font-semibold text-primary" href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>
      </section>

      <section id="privacy" className={card}>
        {head(<ShieldCheck className="h-5 w-5" />, 'Privacy', 'Your choices and rights under India\'s Digital Personal Data Protection Act.')}
        <PrivacySettings />
      </section>

      <section id="account" className={card}>
        {head(<UserX className="h-5 w-5" />, 'Your account', `Signed in as ${me.user.email}.`)}
        <div className="flex flex-col gap-3 rounded-2xl border border-border p-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1"><div className="text-sm font-bold">Export my data</div>
            <p className="text-xs text-muted-foreground">Your library, tags, bookmarks, collections, share links, browsers, scans, transcriptions and credits as JSON.</p></div>
          <Button variant="secondary" loading={exporting} onClick={exportData}><Download className="h-3.5 w-3.5" />Download</Button>
        </div>
        <div className="flex flex-col gap-3 rounded-2xl border border-rose-500/30 p-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1"><div className="text-sm font-bold text-rose-600 dark:text-rose-400">Delete my account</div>
            <p className="text-xs text-muted-foreground">Removes your library, bookmarks, collections, share links and connected browsers, and signs you out everywhere. This can't be undone. Payment records are kept for accounting. Creator data in the shared catalogue stays.</p></div>
          <Button variant="outline-danger" onClick={() => setDeleting(true)}>Delete account</Button>
        </div>
      </section>

      <ConfirmDialog open={deleting} title="Delete your account?" danger confirmLabel="Delete my account" busy={deleteBusy} typeToConfirm={me.user.email}
        body={<p>Your library, bookmarks, collections (and their share links) and connected browsers are deleted, and you are signed out everywhere. If you have a paid plan, cancel it in Billing first. If you signed in more than 10 minutes ago, you'll be asked to sign in again first.</p>}
        onCancel={() => setDeleting(false)} onConfirm={deleteAccount} />

      <ConfirmDialog open={!!revokeApp} title={`Disconnect ${revokeApp?.name ?? ''}?`} danger confirmLabel="Disconnect"
        body={<p>It can no longer read your research or act for you. You can connect it again any time.</p>} onCancel={() => setRevokeApp(null)}
        onConfirm={async () => {
          const a = revokeApp;
          setRevokeApp(null);
          if (!a) return;
          try { await api(`/api/connected-apps/${a.id}`, { method: 'DELETE' }); toast.success(`${a.name} disconnected`); } catch (e) { toast.error('Could not disconnect', { description: (e as Error).message }); }
          void loadApps();
        }} />

      <ConfirmDialog open={!!revoke} title={`Disconnect ${revoke?.name ?? ''}?`} danger confirmLabel="Disconnect" busy={busy}
        body={<p>The extension in that browser stops working until you connect it again.</p>} onCancel={() => setRevoke(null)} onConfirm={() => revoke && disconnect(revoke)} />
    </div>
  );
}
